/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useMemo, useRef} from 'react';
import {useSelector} from 'react-redux';
import Extent from '@arcgis/core/geometry/Extent';
import isNil from 'lodash.isnil';
import {getAssetCount} from '@ion/core/src/services/ArcGIS';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {getAllAccountIds} from '@ion/core/src/services/FetchWorkerUtils';
import {safeJSONStringify} from '@ion/core/src/utils/JSONUtils';
import {sortByKey} from '@ion/core/src/utils/ObjectUtil';
import {EXTENT_FACTOR, mapViewGoToOptions} from '@ion/dls/src/maps/constants';
import QueryKey, {createKey} from '@ion/query/src/config/QueryKeys';
import {cacheOnFetch, getStaleTime} from '@ion/query/src/utils/helpers';
import {
  globalGroupIdSelector,
  globalGroupStateSelector
} from '../../../../../store/selectors/global-group-chooser-selectors';

type Params = {
  groups: number[];
};

/**
 * Custom React hook that manages map view extents based on global group selections.
 * It fetches asset counts and corresponding extents for the selected groups,
 * then automatically adjusts the map view to fit the new extent when group changes occur.
 *
 * @param view - The active ArcGIS MapView instance.
 * @returns An object containing:
 *  - `getGroupAssetCountAndExtent`: a method to fetch asset counts and extents.
 *  - `queryParams`: computed parameters for the current global group state.
 * 
 * ```typescript
 * import useViewExtent from '../hooks/useViewExtent';
 * ...
 * ...
 * useViewExtent(view);
 * 
 * ```
 */
const useViewExtent = (view: __esri.MapView) => {
  const globalGroupId    = useSelector(globalGroupIdSelector);
  const globalGroupState = useSelector(globalGroupStateSelector);
  const globalGroupIdRef = useRef(globalGroupId);

  /**
   * useMemo hook to compute query parameters (group account IDs)
   * whenever the global group state or ID changes.
   */
  const queryParams = useMemo(() => {
    let accountIds: number[] = [];
    if (!isNil(globalGroupId)) {
      accountIds = getAllAccountIds(globalGroupState, entityId(globalGroupId));
    };
    const params = {
      groups : accountIds
    };
    return {params};
  }, [globalGroupState, globalGroupId]);

  /**
   * Fetches the asset count and extent for the given group(s).
   * Uses caching (via cacheOnFetch) to prevent redundant network requests
   * for previously queried groups.
   *
   * @param params - Object containing the groups to query.
   * @returns A promise resolving with the asset count and extent geometry.
   */
  const getGroupAssetCountAndExtent = async(params: Params): Promise<{count: number, extent: __esri.Extent}> => {
    const {groups} = params;
    const queryKey = safeJSONStringify(sortByKey(createKey(QueryKey.GET_CVS_ASSETS_COUNT, groups))) as any;
    const {count, extent} = await cacheOnFetch({
      queryFn             : async() => await getAssetCount(params),
      staleTime           : getStaleTime(0, false, false) as number,
      defaultErrorHandler : true,
      queryKey
    }) as any;
    return {count, extent};
  };

  /**
   * useEffect hook that triggers when the global group changes.
   * It fetches the new extent and adjusts the map view to display
   * all assets belonging to the selected group(s).
   */
  useEffect(() => {
    (async function() {
      const {params} = queryParams;
      if (view && (globalGroupIdRef.current !== globalGroupId)) {
        const {count, extent} = await getGroupAssetCountAndExtent(params as any);
        if (count && extent) {
          const expandedExtent  = new Extent(extent).expand(EXTENT_FACTOR);
          view?.goTo?.(expandedExtent, mapViewGoToOptions);
        }
      }
      globalGroupIdRef.current = globalGroupId;
    })();
  }, [globalGroupId, queryParams, view]);

  return {getGroupAssetCountAndExtent, queryParams};
};

export default useViewExtent;
